const ROOT_SELECTOR = '[data-purchase-navigation]';
const OPTION_UI_SELECTOR = '[data-shopify-option-ui][data-option-location="form"]';

class PurchaseNavigation {
  constructor(root) {
    this.root = root;
    this.footerSummary = root.querySelector('[data-purchase-navigation-footer-summary]');
    this.overlay = root.querySelector('[data-purchase-navigation-overlay]');
    this.drawer = root.querySelector('[data-purchase-navigation-drawer]');
    this.controls = root.querySelector('[data-purchase-navigation-controls]');
    this.closeButton = root.querySelector('[data-purchase-navigation-close]');
    this.addButton = root.querySelector('[data-purchase-navigation-add]');
    this.message = root.querySelector('[data-purchase-navigation-message]');
    this.productFormComponent = null;
    this.nativeForm = null;
    this.lastFocused = null;
    this.rebuildTimer = 0;
    this.observer = null;
  }

  init() {
    this.findNativeForm();
    if (!this.productFormComponent || !this.nativeForm) return;

    this.root.hidden = false;
    this.root.classList.add('purchase-navigation--ready');

    // Ritual既存追従バーから購入ドロワーを開く。
    // 追従バーはバリエーション変更時に再描画されるため、
    // documentへのイベント委譲で処理する。
    document.addEventListener('click', (event) => {
      if (!(event.target instanceof Element)) return;

      const opener = event.target.closest('[data-purchase-navigation-pc-open]');
      if (!opener) return;

      event.preventDefault();
      this.open();
    });
    this.closeButton?.addEventListener('click', () => this.close());
    this.overlay?.addEventListener('click', () => this.close());
    this.addButton?.addEventListener('click', () => this.addToCart());
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.isOpen()) this.close();
    });

    document.addEventListener('change', (event) => {
      if (!(event.target instanceof Element)) return;
      if (
        event.target.closest('variant-picker') ||
        event.target.closest('quantity-selector-component') ||
        event.target.closest('[data-shopify-option-ui]')
      ) {
        this.scheduleRefresh();
      }
    });

    document.addEventListener('input', (event) => {
      if (!(event.target instanceof Element)) return;
      if (event.target.matches('quantity-selector-component input[name="quantity"]')) {
        this.scheduleRefresh();
      }
    });

    this.observeProductForm();
    this.refresh();
  }

  findNativeForm() {
    const candidates = [...document.querySelectorAll('product-form-component')];
    this.productFormComponent =
      candidates.find((component) => !component.closest('dialog, quick-add-component, product-card')) ||
      candidates[0] ||
      null;
    this.nativeForm = this.productFormComponent?.querySelector('form[data-type="add-to-cart-form"]') || null;
  }

  observeProductForm() {
    this.observer?.disconnect();
    const region =
      this.productFormComponent?.closest('.product-information, [data-section-type="product-information"], .shopify-section') ||
      this.productFormComponent;

    if (!region) return;

    this.observer = new MutationObserver(() => {
      this.findNativeForm();
      this.scheduleRefresh();
    });
    this.observer.observe(region, { childList: true, subtree: true, attributes: true });
  }

  scheduleRefresh() {
    window.clearTimeout(this.rebuildTimer);
    this.rebuildTimer = window.setTimeout(() => this.refresh(), 80);
  }

  refresh() {
    this.findNativeForm();
    if (!this.nativeForm) return;

    const data = this.readCurrentSelection();
    this.renderSummary(data);
    if (this.isOpen()) this.renderControls();
  }

  readCurrentSelection() {
    const variants = [];
    const picker = this.findVariantPicker();

    if (picker) {
      for (const fieldset of picker.querySelectorAll('fieldset.variant-option')) {
        const label = fieldset.querySelector('legend')?.textContent?.trim() || '選択肢';
        const checked = fieldset.querySelector('input[type="radio"]:checked');
        if (checked) variants.push({ label, value: checked.value });
      }

      for (const select of picker.querySelectorAll('select')) {
        const label =
          select.closest('.variant-option')?.querySelector('label, legend')?.textContent?.trim() ||
          select.getAttribute('aria-label') ||
          '選択肢';
        const selected = select.selectedOptions[0];
        if (selected) variants.push({ label, value: selected.textContent.trim() });
      }

      for (const single of picker.querySelectorAll('[data-testid="variant-option-single"]')) {
        const clone = single.cloneNode(true);
        const value = clone.querySelector('[data-testid="variant-option-single-value"]')?.textContent?.trim() || '';
        clone.querySelector('[data-testid="variant-option-single-value"]')?.remove();
        const label = clone.textContent.trim() || '選択肢';
        if (value) variants.push({ label, value });
      }
    }

    const quantityInput = this.nativeForm.querySelector('quantity-selector-component input[name="quantity"]');
    const quantity = Math.max(1, Number(quantityInput?.value || 1));

    const options = [];
    for (const ui of document.querySelectorAll(OPTION_UI_SELECTOR)) {
      const label = ui.querySelector('.shopify-option-ui__title')?.textContent?.trim() || 'オプション';
      const selectedNames = [];

      for (const input of ui.querySelectorAll('input[data-option-variant-id]:checked:not(:disabled)')) {
        const name = input.closest('label')?.querySelector('.shopify-option-ui__product-title')?.textContent?.trim();
        if (name) selectedNames.push(name);
      }

      const select = ui.querySelector('select[data-option-variant-select]');
      if (select?.value) {
        const name = select.selectedOptions[0]?.textContent?.replace(/（[^）]*円）(?:［在庫切れ］)?$/, '')?.trim();
        if (name) selectedNames.push(name);
      }

      options.push({ label, values: selectedNames });
    }

    return { variants, quantity, options };
  }

  renderSummary(data) {
    const parts = data.variants.map((item) => item.value);
    const selectedOptionCount = data.options.reduce((sum, item) => sum + item.values.length, 0);

    if (selectedOptionCount > 0) parts.push(`オプション${selectedOptionCount}件`);
    parts.push(`数量${data.quantity}`);

    const summaryText = parts.filter(Boolean).join(' ／ ') || '商品を選択してください';
    if (this.footerSummary) {
      this.footerSummary.replaceChildren();

      for (const item of data.variants) {
        this.footerSummary.append(this.createSummaryRow(item.label, item.value));
      }

      for (const option of data.options) {
        this.footerSummary.append(
          this.createSummaryRow(option.label, option.values.length ? option.values.join('、') : '不要')
        );
      }

      this.footerSummary.append(this.createSummaryRow('数量', String(data.quantity)));
    }
  }

  createSummaryRow(label, value) {
    const row = document.createElement('div');
    row.className = 'purchase-navigation__summary-row';

    const key = document.createElement('span');
    key.className = 'purchase-navigation__summary-key';
    key.textContent = label;

    const val = document.createElement('span');
    val.className = 'purchase-navigation__summary-value';
    val.textContent = value;

    row.append(key, val);
    return row;
  }

  findVariantPicker() {
    const section =
      this.productFormComponent?.closest('.product-information, [data-section-type="product-information"], .shopify-section') ||
      document;
    return section.querySelector('variant-picker[ref="mainVariantPicker"], variant-picker');
  }

  open() {
    this.lastFocused = document.activeElement;
    this.renderControls();
    this.renderSummary(this.readCurrentSelection());

    this.overlay.hidden = false;
    this.drawer.hidden = false;
    requestAnimationFrame(() => {
      this.root.classList.add('purchase-navigation--open');
      document.documentElement.classList.add('purchase-navigation-lock');
      document.documentElement.setAttribute('scroll-lock', '');
      this.closeButton?.focus();
    });
  }

  close() {
    this.root.classList.remove('purchase-navigation--open');
    document.documentElement.classList.remove('purchase-navigation-lock');
    document.documentElement.removeAttribute('scroll-lock');

    window.setTimeout(() => {
      if (this.overlay) this.overlay.hidden = true;
      if (this.drawer) this.drawer.hidden = true;
    }, 240);

    if (this.lastFocused instanceof HTMLElement) this.lastFocused.focus();
  }

  isOpen() {
    return this.root.classList.contains('purchase-navigation--open');
  }

  renderControls() {
    if (!this.controls) return;
    this.controls.replaceChildren();

    const picker = this.findVariantPicker();
    if (picker) this.renderVariantControls(picker);

    this.renderOptionControls();
    this.renderQuantityControl();
  }

  renderVariantControls(picker) {
    for (const fieldset of picker.querySelectorAll('fieldset.variant-option')) {
      const legend = fieldset.querySelector('legend')?.textContent?.trim() || '選択肢';
      const group = this.createControlGroup(legend);
      const choices = document.createElement('div');
      choices.className = 'purchase-navigation__choice-grid';

      for (const original of fieldset.querySelectorAll('input[type="radio"]')) {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'purchase-navigation__choice-button';
        button.textContent =
          original.closest('label')?.querySelector('.variant-option__button-label__text')?.textContent?.trim() ||
          original.getAttribute('aria-label') ||
          original.value;
        button.dataset.selected = original.checked ? 'true' : 'false';
        button.disabled = original.getAttribute('aria-disabled') === 'true';

        button.addEventListener('click', () => {
          if (original.disabled || original.getAttribute('aria-disabled') === 'true') return;
          original.click();
          this.setMessage('選択内容を更新しています。');
          window.setTimeout(() => {
            this.setMessage('');
            this.refresh();
          }, 450);
        });
        choices.append(button);
      }

      group.append(choices);
      this.controls.append(group);
    }

    for (const original of picker.querySelectorAll('select')) {
      const label =
        original.closest('.variant-option')?.querySelector('label, legend')?.textContent?.trim() ||
        original.getAttribute('aria-label') ||
        '選択肢';
      const group = this.createControlGroup(label);
      const select = original.cloneNode(true);
      select.className = 'purchase-navigation__select';
      select.removeAttribute('id');
      select.removeAttribute('name');
      select.addEventListener('change', () => {
        original.value = select.value;
        original.dispatchEvent(new Event('change', { bubbles: true }));
        this.setMessage('選択内容を更新しています。');
        window.setTimeout(() => {
          this.setMessage('');
          this.refresh();
        }, 450);
      });
      group.append(select);
      this.controls.append(group);
    }
  }

  renderOptionControls() {
    for (const ui of document.querySelectorAll(OPTION_UI_SELECTOR)) {
      const title = ui.querySelector('.shopify-option-ui__title')?.textContent?.trim() || 'オプション';
      const group = this.createControlGroup(title);
      const list = document.createElement('div');
      list.className = 'purchase-navigation__option-list';

      for (const original of ui.querySelectorAll('input[data-option-variant-id]')) {
        const label = document.createElement('label');
        label.className = 'purchase-navigation__option-choice';

        const input = document.createElement('input');
        input.type = 'checkbox';
        input.checked = original.checked;
        input.disabled = original.disabled;

        const name = document.createElement('span');
        name.textContent =
          original.closest('label')?.querySelector('.shopify-option-ui__product-title')?.textContent?.trim() ||
          original.getAttribute('aria-label') ||
          'オプション';

        const price = document.createElement('span');
        price.className = 'purchase-navigation__option-price';
        price.textContent =
          original.closest('label')?.querySelector('.shopify-option-ui__product-price')?.textContent?.trim() || '';

        input.addEventListener('change', () => {
          if (original.checked !== input.checked) original.click();
          this.refresh();
        });

        label.append(input, name, price);
        list.append(label);
      }

      const originalSelect = ui.querySelector('select[data-option-variant-select]');
      if (originalSelect) {
        const select = originalSelect.cloneNode(true);
        select.className = 'purchase-navigation__select';
        select.removeAttribute('data-option-variant-select');
        select.addEventListener('change', () => {
          originalSelect.value = select.value;
          originalSelect.dispatchEvent(new Event('change', { bubbles: true }));
          this.refresh();
        });
        list.append(select);
      }

      group.append(list);
      this.controls.append(group);
    }
  }

  renderQuantityControl() {
    const original = this.nativeForm?.querySelector('quantity-selector-component input[name="quantity"]');
    if (!original) return;

    const group = this.createControlGroup('数量');
    const wrapper = document.createElement('div');
    wrapper.className = 'purchase-navigation__quantity';

    const minus = document.createElement('button');
    minus.type = 'button';
    minus.textContent = '−';
    minus.setAttribute('aria-label', '数量を減らす');

    const input = document.createElement('input');
    input.type = 'number';
    input.value = original.value;
    input.min = original.min || '1';
    input.step = original.step || '1';
    if (original.max) input.max = original.max;
    input.setAttribute('aria-label', '数量');

    const plus = document.createElement('button');
    plus.type = 'button';
    plus.textContent = '＋';
    plus.setAttribute('aria-label', '数量を増やす');

    const apply = (value) => {
      const min = Number(input.min || 1);
      const max = input.max ? Number(input.max) : Number.POSITIVE_INFINITY;
      const step = Number(input.step || 1);
      const next = Math.min(max, Math.max(min, value));
      input.value = String(next);
      original.value = String(next);
      original.dispatchEvent(new Event('input', { bubbles: true }));
      original.dispatchEvent(new Event('change', { bubbles: true }));
      original.dispatchEvent(new Event('blur', { bubbles: true }));
      this.renderSummary(this.readCurrentSelection());
    };

    minus.addEventListener('click', () => apply(Number(input.value || 1) - Number(input.step || 1)));
    plus.addEventListener('click', () => apply(Number(input.value || 1) + Number(input.step || 1)));
    input.addEventListener('change', () => apply(Number(input.value || 1)));

    wrapper.append(minus, input, plus);
    group.append(wrapper);
    this.controls.append(group);
  }

  createControlGroup(title) {
    const group = document.createElement('fieldset');
    group.className = 'purchase-navigation__control-group';

    const legend = document.createElement('legend');
    legend.textContent = title;
    group.append(legend);
    return group;
  }

  addToCart() {
    this.findNativeForm();
    if (!this.nativeForm) {
      this.setMessage('商品フォームが見つかりません。ページを再読み込みしてください。', true);
      return;
    }

    const submitter =
      this.nativeForm.querySelector('button[type="submit"]:not([disabled])') ||
      this.nativeForm.querySelector('input[type="submit"]:not([disabled])');

    if (!submitter) {
      this.setMessage('現在選択している商品はカートに追加できません。', true);
      return;
    }

    this.setMessage('カートへ追加しています。');
    this.close();

    if (typeof this.nativeForm.requestSubmit === 'function') {
      this.nativeForm.requestSubmit(submitter);
    } else {
      submitter.click();
    }
  }

  setMessage(text, isError = false) {
    if (!this.message) return;
    this.message.textContent = text;
    this.message.classList.toggle('purchase-navigation__message--error', isError);
  }
}

function initializePurchaseNavigation() {
  const root = document.querySelector(ROOT_SELECTOR);
  if (!(root instanceof HTMLElement) || root.dataset.initialized === 'true') return;

  root.dataset.initialized = 'true';
  new PurchaseNavigation(root).init();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializePurchaseNavigation, { once: true });
} else {
  initializePurchaseNavigation();
}
